import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjsLocaleEs from 'dayjs/locale/es';
import { logger } from './logger';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// Register Spanish locale directly on this moment instance to avoid pnpm
// module-isolation issues (moment-timezone bundles its own private moment).
moment.defineLocale('es', {
  months: 'enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre'.split('_'),
  monthsShort: 'ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.'.split('_'),
  weekdays: 'domingo_lunes_martes_miércoles_jueves_viernes_sábado'.split('_'),
  weekdaysShort: 'dom._lun._mar._mié._jue._vie._sáb.'.split('_'),
  weekdaysMin: 'do_lu_ma_mi_ju_vi_sá'.split('_'),
  longDateFormat: { LT: 'H:mm', LTS: 'H:mm:ss', L: 'DD/MM/YYYY', LL: 'D [de] MMMM [de] YYYY', LLL: 'D [de] MMMM [de] YYYY H:mm', LLLL: 'dddd, D [de] MMMM [de] YYYY H:mm' },
  meridiem: (hours: number) => hours < 12 ? 'a. m.' : 'p. m.',
  meridiemParse: /a\. ?m\.|p\. ?m\./i,
  isPM: (input: string) => /^p/i.test(input),
  relativeTime: { future: 'en %s', past: 'hace %s', s: 'unos segundos', ss: '%d segundos', m: 'un minuto', mm: '%d minutos', h: 'una hora', hh: '%d horas', d: 'un día', dd: '%d días', w: 'una semana', ww: '%d semanas', M: 'un mes', MM: '%d meses', y: 'un año', yy: '%d años' },
  ordinal: (number: number) => `${number}º`,
  week: { dow: 1, doy: 4 },
});
// defineLocale activates the new locale — reset to English as the app default.
moment.locale('en');

let _currentLang = 'en';

export const setDateLocale = (lang: string): void => {
  _currentLang = lang;
  moment.locale(lang);
  dayjs.locale(lang === 'es' ? dayjsLocaleEs : 'en');
};

export const getTimeFromNow = (date: string) => {
  // e.g., "2 days ago"
  return dayjs(date).fromNow();
};

/**
 * Returns a locale-aware relative time string (e.g. "hace 8 días") for dates
 * within 7 days, or a locale-aware short date (e.g. "4 may. 2025") for older ones.
 * Replaces the English-keyword pattern used in CommentItem / CommentReplies.
 */
export const formatRelativeTime = (createdAt: string): string => {
  const m = moment(createdAt).locale(_currentLang);
  const diffDays = moment().diff(moment(createdAt), 'days');
  return diffDays >= 7 ? m.format('ll') : m.fromNow();
};

/**
 * Formats a date according to the specified format and timezone
 *
 */
export const formatDateTime = (date: string, timezone: string, format: string): string => {
  try {
    // Create moment instance from the input date
    const momentDate = moment.utc(date);

    // Check if the date is valid
    if (!momentDate.isValid()) {
      throw new Error('Invalid date provided');
    }
    // Convert to the specified timezone and format

    // logger.log('formatDateTime', date, timezone, format, momentDate.tz(timezone).format(format))
    return momentDate.tz(timezone).locale(_currentLang).format(format);
  } catch (error) {
    logger.error('Error formatting date:', error);
    throw new Error(
      `Failed to format date: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

export const parseToTimeZone = (date: string | Date, timezone: string): string => {
  const momentDate = moment.utc(date);
  const parsedDate = momentDate.tz(timezone).format('YYYY-MM-DDTHH:mm');
  return parsedDate;
};

export const momentToTimeZone = (date: string | Date, timezone: string): Date => {
  return moment.tz(moment(date).format('YYYY-MM-DDTHH:mm'), timezone).toDate();
};

/**
 * Formats a date according to the specified format and timezone
 *
 */
export const formatDateTimeToString = (date: string, timezone: string): string => {
  try {
    // Create moment instance from the input date
    const momentDate = moment(date);

    // Check if the date is valid
    if (!momentDate.isValid()) {
      throw new Error('Invalid date provided');
    }
    // Convert to the specified timezone
    return momentDate.tz(timezone).toString();
  } catch (error) {
    logger.error('Error formatting date:', error);
    throw new Error(
      `Failed to format date: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Checks if a date is after another date
 */
export const isAfterCurrentTime = (compareDate: dayjs.Dayjs): boolean => {
  return dayjs().isAfter(compareDate);
};

/**
 * Checks if a date is before the current time
 */
export const isBeforeCurrentTime = (compareDate: dayjs.Dayjs): boolean => {
  return dayjs().isBefore(compareDate);
};


export const isSameDayUtil = (date1: string, date2: string): boolean => {
  return dayjs(date1).isSame(dayjs(date2), 'day');
};
